import React, { useMemo } from "react";
import { Wheat, Leaf, Drumstick, Boxes } from "lucide-react";

const formatPct = (value) => {
  if (value == null) return "-";
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`;
};

const formatGrams = (value) => {
  if (value == null) return "-";
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)} g`;
};

const toPerPortionGrams = (pct, portionSize) => {
  if (pct == null) return null;
  const size = Number.parseFloat(portionSize);
  if (!Number.isFinite(size) || size <= 0) return null;
  return (size * pct) / 100;
};

const normalizeApiValues = (values) => {
  if (!values || typeof values !== "object") return null;
  const toNumOrNull = (v) => {
    if (v == null) return null;
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const wholegrain = toNumOrNull(values.wholegrain_percentage);
  const addedSugar = toNumOrNull(values.added_sugar_percentage);
  const green = toNumOrNull(values.green_percentage);
  const redMeat = toNumOrNull(values.red_meat_percentage);
  if (wholegrain == null && addedSugar == null && green == null && redMeat == null) {
    return null;
  }
  return {
    wholegrain: wholegrain ?? 0,
    addedSugar: addedSugar ?? 0,
    green: green ?? 0,
    redMeat,
    redMeatUnknown: redMeat == null,
  };
};

const CompositionPercentagesCard = ({ values, portionSize, t = (s) => s }) => {
  const apiValues = useMemo(() => normalizeApiValues(values), [values]);
  if (!apiValues) {
    return null;
  }

  const rows = [
    {
      label: "Fullkorn",
      value: apiValues.wholegrain,
      icon: <Wheat className="w-4 h-4" />,
      iconBgClass: "bg-amber-100",
      iconTextClass: "text-amber-600",
    },
    {
      label: "Tillsatt socker",
      value: apiValues.addedSugar,
      icon: <Boxes className="w-4 h-4" />,
      iconBgClass: "bg-violet-100",
      iconTextClass: "text-violet-600",
    },
    {
      label: "Grönsaker, frukt eller bär",
      value: apiValues.green,
      icon: <Leaf className="w-4 h-4" />,
      iconBgClass: "bg-emerald-100",
      iconTextClass: "text-emerald-600",
    },
    {
      label: "Rött kött",
      value: apiValues.redMeatUnknown ? null : apiValues.redMeat,
      icon: <Drumstick className="w-4 h-4" />,
      iconBgClass: "bg-rose-100",
      iconTextClass: "text-rose-600",
    },
  ];

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 mt-4">
      <div className="flex items-center justify-end gap-4 text-xs font-semibold text-gray-500 mb-2 px-0.5">
        <span className="w-14 text-right">%</span>
        <span className="w-16 text-right">{t("Per portion")}</span>
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-gray-700 inline-flex items-center gap-2 min-w-0">
              <span
                aria-hidden="true"
                className={`inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${row.iconBgClass} ${row.iconTextClass}`}
              >
                {row.icon}
              </span>
              <span className="truncate">{t(row.label)}:</span>
            </span>
            <span className={`inline-flex items-center gap-4 shrink-0 font-medium ${row.iconTextClass}`}>
              <span className="w-14 text-right tabular-nums">{formatPct(row.value)}</span>
              <span className="w-16 text-right tabular-nums">
                {formatGrams(toPerPortionGrams(row.value, portionSize))}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompositionPercentagesCard;
